/**
 * Client Information tab — additional intake fields not in core profile.
 * Includes PCP, pharmacy, hospice, insurance, POA, living situation, etc.
 * Singleton record per client; PUT upserts.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import TagInput from "@/components/TagInput";

const SECTIONS = [
  {
    eyebrow: "Identity & preferences",
    fields: [
      { k: "preferred_name", l: "Preferred name", type: "text" },
      { k: "religion", l: "Religion", type: "text" },
      { k: "primary_language", l: "Primary language", type: "text" },
      { k: "marital_status", l: "Marital status", type: "select", options: [
        ["", "—"], ["single", "Single"], ["married", "Married"],
        ["widowed", "Widowed"], ["divorced", "Divorced"], ["partnered", "Partnered"],
      ]},
    ],
  },
  {
    eyebrow: "Insurance & coverage",
    fields: [
      { k: "insurance_primary", l: "Primary insurance", type: "text" },
      { k: "insurance_primary_id", l: "Primary policy #", type: "text" },
      { k: "insurance_secondary", l: "Secondary insurance", type: "text" },
      { k: "insurance_secondary_id", l: "Secondary policy #", type: "text" },
      { k: "medicare_number", l: "Medicare #", type: "text" },
    ],
  },
  {
    eyebrow: "Primary care provider",
    fields: [
      { k: "pcp_name", l: "PCP name", type: "text" },
      { k: "pcp_phone", l: "PCP phone", type: "tel" },
      { k: "pcp_address", l: "PCP address", type: "text", full: true },
    ],
  },
  {
    eyebrow: "Pharmacy",
    fields: [
      { k: "pharmacy_name", l: "Pharmacy name", type: "text" },
      { k: "pharmacy_phone", l: "Pharmacy phone", type: "tel" },
      { k: "pharmacy_address", l: "Pharmacy address", type: "text", full: true },
    ],
  },
  {
    eyebrow: "Hospice & hospital",
    fields: [
      { k: "hospice_provider", l: "Hospice provider", type: "text" },
      { k: "hospice_phone", l: "Hospice phone", type: "tel" },
      { k: "preferred_hospital", l: "Preferred hospital", type: "text", full: true },
    ],
  },
  {
    eyebrow: "Power of attorney & legal",
    fields: [
      { k: "has_poa", l: "POA on file", type: "checkbox" },
      { k: "poa_type", l: "POA type", type: "select", options: [
        ["", "—"], ["medical", "Medical"], ["financial", "Financial"], ["both", "Both"],
      ]},
      { k: "poa_name", l: "POA name", type: "text" },
      { k: "poa_phone", l: "POA phone", type: "tel" },
      { k: "legal_guardian_name", l: "Legal guardian name", type: "text" },
      { k: "legal_guardian_phone", l: "Guardian phone", type: "tel" },
    ],
  },
  {
    eyebrow: "Living situation",
    fields: [
      { k: "living_arrangement", l: "Living arrangement", type: "select", options: [
        ["", "—"], ["alone", "Alone"], ["with_spouse", "With spouse"],
        ["with_family", "With family"], ["with_roommate", "With roommate"],
        ["assisted_living", "Assisted living"], ["nursing_home", "Nursing home"],
        ["group_home", "Group home"], ["other", "Other"],
      ]},
      { k: "pets", l: "Pets", type: "text" },
      { k: "home_access_notes", l: "Home access notes (gate codes, key location)", type: "textarea", full: true },
    ],
  },
];

function Field({ field, value, onChange }) {
  const id = `ci-${field.k}`;
  const common = "input-clinical mt-1";
  if (field.type === "select") {
    return (
      <div className={field.full ? "sm:col-span-2" : ""}>
        <label htmlFor={id} className="text-xs text-clinical-subtext">{field.l}</label>
        <select id={id} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className={common} data-testid={id}>
          {field.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <div className={field.full ? "sm:col-span-2" : ""}>
        <label className="flex items-center gap-2 text-sm mt-5">
          <input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
            data-testid={id} />
          {field.l}
        </label>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className={field.full ? "sm:col-span-2" : ""}>
        <label htmlFor={id} className="text-xs text-clinical-subtext">{field.l}</label>
        <textarea id={id} rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className={`${common} resize-y`} data-testid={id} />
      </div>
    );
  }
  return (
    <div className={field.full ? "sm:col-span-2" : ""}>
      <label htmlFor={id} className="text-xs text-clinical-subtext">{field.l}</label>
      <input id={id} type={field.type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className={common} data-testid={id} />
    </div>
  );
}

export default function ClientInfoTab({ clientId }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/clients/${clientId}/client-info`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/client-info`, data);
      toast.success("Client information saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-info-tab">
      {SECTIONS.map((section) => (
        <div key={section.eyebrow} className="card-surface p-4">
          <h3 className="label-eyebrow mb-3">{section.eyebrow}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {section.fields.map((f) => (
              <Field key={f.k} field={f} value={data[f.k]}
                onChange={(v) => set(f.k, v)} />
            ))}
          </div>
        </div>
      ))}

      <div className="card-surface p-4">
        <h3 className="label-eyebrow mb-3">Other languages</h3>
        <TagInput
          value={data.secondary_languages || []}
          onChange={(v) => set("secondary_languages", v)}
          suggestions={["en", "es", "fr", "ar", "pt", "tl", "vi", "zh", "hi", "ko"]}
          placeholder="Languages spoken besides primary…"
          testid="ci-secondary-languages"
        />
        <label className="flex items-center gap-2 text-sm mt-3">
          <input type="checkbox" checked={!!data.interpreter_needed}
            onChange={(e) => set("interpreter_needed", e.target.checked)}
            data-testid="ci-interpreter-needed" />
          Interpreter needed
        </label>
      </div>

      <div className="card-surface p-4 space-y-3">
        <div>
          <label htmlFor="ci-recent-hosp" className="text-xs text-clinical-subtext">Recent hospitalizations</label>
          <textarea id="ci-recent-hosp" rows={3}
            value={data.recent_hospitalizations || ""}
            onChange={(e) => set("recent_hospitalizations", e.target.value)}
            placeholder="Date · facility · reason · discharge summary"
            className="input-clinical resize-y mt-1" data-testid="ci-recent-hospitalizations" />
        </div>
        <div>
          <label htmlFor="ci-extra-notes" className="text-xs text-clinical-subtext">Additional notes</label>
          <textarea id="ci-extra-notes" rows={3}
            value={data.additional_notes || ""}
            onChange={(e) => set("additional_notes", e.target.value)}
            className="input-clinical resize-y mt-1" data-testid="ci-additional-notes" />
        </div>
      </div>

      <div className="sticky bottom-0 bg-white pt-3">
        <button type="button" onClick={save} disabled={saving} className="btn-primary"
          data-testid="ci-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save client information
        </button>
      </div>
    </div>
  );
}
